/**
 * Render-time rewrite of stored media URLs onto the CDN host.
 *
 * This is the admin-side twin of the storefront's `src/utils/toCdnUrl.ts`. The two
 * MUST agree: they mirror the same server-side allowlist (integrations-service
 * `storage-router.ts`), and a divergence would either leak a private prefix onto a
 * bucket that does not hold it, or leave public media on the expensive origin.
 *
 * Media URLs are persisted ABSOLUTE in Mongo — `https://<bucket>.s3.<region>
 * .amazonaws.com/shops/<id>/catalog/x.jpg` — so moving new uploads to a
 * cheap-egress bucket changes nothing for the catalogue that already exists.
 * Rewriting the host as the response is read fixes that without a database
 * migration; nothing is written, so rollback is unsetting one build value.
 *
 * ---------------------------------------------------------------------------
 * What this deliberately REFUSES to rewrite
 * ---------------------------------------------------------------------------
 * The app bucket is four workloads sharing one namespace. Public media
 * (`catalog/`, `FILE/`) sits next to private documents (`INVOICE/`, `REPORT/`,
 * `CREDIT_NOTE/`, `ACADEMIC_DOCUMENT/`, `*_IMPORT_JOB/`, `LEDGER_EXPORT/`), and
 * admin payloads carry both — an order response has product imagery AND an
 * invoice URL. A blanket host swap would point invoice links at a bucket that
 * does not hold them.
 *
 * So the allowlist is POSITIVE: only `catalog/` and `FILE/`, with an optional
 * `shops/<shopId>/` prefix. An unrecognised prefix is never rewritten, which
 * means a new private prefix cannot leak in by omission.
 *
 * It is also a no-op unless configured, which is what lets it ship dark.
 */

import { environment } from '../../../environments/environment';

const trimTrailingSlash = (s: string): string => s.replace(/\/+$/, '');

/** Target host, e.g. `https://cdn.unicornstore.app`. */
const cdnBase = (): string => trimTrailingSlash(((environment as any).mediaCdnBaseUrl as string) || '');

/**
 * Hosts whose objects have been mirrored to the CDN, comma-separated.
 *
 * Explicit rather than "any host": a URL is only rewritten when we know the same
 * key exists on the CDN. Rewriting an unknown host would turn a working image
 * into a 404.
 */
const legacyBases = (): string[] =>
  (((environment as any).mediaLegacyBaseUrls as string) || '')
    .split(',')
    .map((s) => trimTrailingSlash(s.trim()))
    .filter(Boolean);

/** Mirrors the server-side public allowlist (integrations `storage-router.ts`). */
const PUBLIC_FOLDERS = new Set(['catalog', 'FILE']);

export const isPublicMediaKey = (key: string): boolean => {
  const withoutShop = key.replace(/^\/+/, '').replace(/^shops\/[^/]+\//, '');
  return PUBLIC_FOLDERS.has(withoutShop.split('/')[0]);
};

/** True when a CDN host and at least one legacy host are configured. */
export const isCdnRewriteEnabled = (): boolean => !!cdnBase() && legacyBases().length > 0;

/**
 * Rewrite one URL onto the CDN, or return it untouched.
 *
 * Untouched when: rewriting is off, the value is not a string, the host is not a
 * configured legacy host, it is already on the CDN, or the key is not public.
 */
export const toCdnUrl = (url: unknown): string => {
  if (typeof url !== 'string' || !url) return (url as string) ?? '';
  const cdn = cdnBase();
  if (!cdn) return url;
  if (url.startsWith(`${cdn}/`)) return url; // already rewritten

  for (const base of legacyBases()) {
    if (!url.startsWith(`${base}/`)) continue;
    // The key is the ENTIRE remainder. Object keys legitimately contain spaces
    // ("catalog/AirPods/AirPods Pro 3/…") and the upload API does not
    // percent-encode them, so any whitespace-terminated parse truncates the key
    // and silently produces a broken URL.
    const key = url.slice(base.length + 1);
    let decoded: string;
    try {
      decoded = decodeURIComponent(key);
    } catch {
      decoded = key;
    }
    if (!isPublicMediaKey(decoded)) return url;
    return `${cdn}/${key}`;
  }
  return url;
};

/**
 * Rewrite every media URL inside an already-parsed response body.
 *
 * The storefront operates on raw response TEXT because its axios layer exposes
 * it. Angular's HttpClient hands back a parsed object, and re-serialising it to
 * run a string pass would be slower and would risk changing the payload's shape
 * (Date revival, key order, numeric precision). So this walks the structure and
 * only ever replaces string leaves — non-strings are returned by identity.
 *
 * Arrays and plain objects are rebuilt only when a descendant actually changed,
 * so an untouched payload keeps its original references and Angular's change
 * detection sees nothing new.
 */
export const rewriteMediaUrlsDeep = <T>(body: T): T => {
  if (!isCdnRewriteEnabled()) return body;
  return walk(body) as T;
};

const walk = (node: unknown): unknown => {
  if (typeof node === 'string') return toCdnUrl(node);
  if (!node || typeof node !== 'object') return node;

  // Leave anything that is not a plain array/object alone — Date, File, Blob and
  // friends must survive the pass untouched.
  if (Array.isArray(node)) {
    let changed = false;
    const out = node.map((v) => {
      const next = walk(v);
      if (next !== v) changed = true;
      return next;
    });
    return changed ? out : node;
  }

  const proto = Object.getPrototypeOf(node);
  if (proto !== Object.prototype && proto !== null) return node;

  let changed = false;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
    const next = walk(v);
    if (next !== v) changed = true;
    out[k] = next;
  }
  return changed ? out : node;
};

export default toCdnUrl;
